import React, {useEffect, useRef, useState} from 'react';
import {useRouteNode} from 'react-router5';
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import initializeMap from "./custom_map_impl";
import EntityView from "../explore/entities/entityView";
import ContactView from "../explore/contacts/contactView";
import {customMapNotifyMissingNode}  from "../api";

// Inspired from GridRO
function CustomMap() {
  const { t } = useTranslation();
  const { route, router } = useRouteNode("explore.theme.custom_map");
  const app_theme = useSelector((state) => state.theme.value);

  const { data, params: route_params } = route;

  const [fullscreen, setFullscreen] =  useState(false);

  if (! data.custom_map) {
    return <div style={{padding: "10px"}}>{t("No custom map associated to the theme")}</div>
  }

  const { id: theme_id, node_id } = route_params;

  const col2 = (
    node_id
    ? getCol2(node_id, data, router, t)
    : false
  );

  const onClick = (data) => {
    // check id, check type and unselect if same

    let route = "explore.theme.custom_map.node";
    let params = {id: theme_id, node_id: data.id};

    if (data.poi_contact_id) {
      route = "explore.theme.custom_map.contact"
      params.contact_id = data.poi_contact_id
    } else if (data.poi_entity_id) {
      route = "explore.theme.custom_map.entity"
      params.entity_id = data.poi_entity_id
    }

    // console.log("CLICKING: ", data.id);
    // reload? so that the useRouteNode catches it
    router.navigate(route, params); // , {reload: true});
  };

  return <div className={"grid-ro grid-main " + (fullscreen ? "grid-container-fullscreen" : "")}>
    <div className="grid-wrapper">
      <CartoCustom
	app_theme={app_theme}
	onClick={onClick}
	custom_map={data.custom_map}
	onFullScreen={(graph) => { setFullscreen((fullscreen) => !fullscreen) }}
      />
    </div>

    {col2 && <div key={"custom_map_" + node_id} className="grid-col2 custom-map_col2">
      { col2 }
    </div>}
  </div>
}


function buildData(custom_map) {
  const {nodes, links} = custom_map;

  const map_links = links.map(link => {
    // return {from: link.from_id, to: link.to_id};
    return {...link, from: link.from_id, to: link.to_id}
  })

  const map_nodes = nodes.map(node => {
    const style = node.style;
    const image = node.poi_contact?.img || node.poi_entity?.img || node.image;

    let new_style = { image };

    Object.keys(style).forEach((k) => {
      const v = style[k];
      if (["shape"].indexOf(k) !== -1 && v !== null) {
	new_style.selected_shape = v === "rect" ? "rectangle" : v;
	return;
      }

      if (v) {
	new_style[k] = v;
      }
    });

    return {
      id: node.id,
      x: node.xy.x,
      y: node.xy.y,
      aura: node.aura,
      loaded: true,
      // extra
      bound_to_ce: node.poi_contact_id || node.poi_entity_id,
      label: node.label,
      info: node.info,
      //
      context: node.context,
      poi_contact_id: node.poi_contact_id,
      poi_entity_id: node.poi_entity_id,
      style: new_style
    };
  })

  return {
    nodes: map_nodes,
    links: map_links
  }
}

function buildSettings(settings) {
  let aura_style = {};

  (settings.auras || []).forEach((a, idx) => {
    aura_style[a.id] = { nameLegend: a.name, fillColor: a.color || "#AAAAAAAA" };
  })

  const layouts = {
    static: {mode: "static", rowSpacing: 16, rotation: null},
    dynamic: {mode: "dynamic", rowSpacing: 16, rotation: null},
    radial: {mode: "radial", rowSpacing: 16, rotation: null},
    hierarchy: {mode: "hierarchy", rowSpacing: 32, rotation: null},
    hierarchy_lr: {mode: "hierarchy", rowSpacing: 32, rotation: -90},
  }

  return {
    layout: layouts[settings.layout],
    auras: {style: aura_style}
  }
}

function CartoCustom({ onClick, onFullScreen, custom_map, app_theme }) {
  const ref = useRef();

  // const [chartAPI, setChartAPI] = useState();
  const settings = buildSettings({
    layout: custom_map.layout || "static",
    auras: custom_map.auras
  });

  useEffect(() => {
    const data = buildData(custom_map);

    const opts = {
      ...settings,
      element: ref.current,
      // height: ref.current.parentNode.offsetHeight,
      focus_nodes: false,
      linkStyle: custom_map.linkStyle || "straight",
      onClick: onClick || (() => {}),
      onFullScreen
    };

    const chartAPI = initializeMap(data, opts)

    if (app_theme === "dark") {
      chartAPI.chart.customize("dark");
    }

    // setChartAPI(chartAPI);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={ref} style={{height: "100%"}} />
}


function getCol2(node_id_str, {custom_map, contacts, entities, contact, entity}, router, t) {
  const node_id = parseInt(node_id_str, 10);
  const node = custom_map.nodes.find(node => node.id === node_id);

  const onClose = () => {
    // may trigger a setState in unmounted component because of useRouteNode
    // FIXME: weird: can not use reload: true, otherwise app is mounted multiple times, etc.
    router.navigate("explore.theme.custom_map", {id: custom_map.poi_theme_id});
  }

  // bad id
  if (! node) { return false; }

  // const {poi_contact_id, poi_entity_id} = node;

  const getEntityRoute = (entity) => {
    return ["explore.theme.custom_map.entity", {id: custom_map.poi_theme_id, node_id: node.id, entity_id: entity.id}]
  }
  const getContactRoute = (contact) => {
    return ["explore.theme.custom_map.contact", {id: custom_map.poi_theme_id, node_id: node.id, contact_id: contact.id}]
  }

  // /!\ WARNING node is different from entity/contact
  // the node is static, the entity/contact moves as links are being clicked (and thus not referencing the node anymore!)
  if (contact) {
    // const contact = contacts.find(c => c.id == poi_contact_id);
    const component_route = "explore.theme.custom_map.contact";

    return <ContactView key={contact.id} contact={contact}
      component_route={component_route} onClose={onClose}
      getEntityRoute={getEntityRoute} getContactRoute={getContactRoute}
    />
  }

  if (entity) {
    // const entity = entities.find(e => e.id == poi_entity_id);
    const component_route = "explore.theme.custom_map.entity";

    return <EntityView key={entity.id} entity={entity}
      component_route={component_route} onClose={onClose}
      getEntityRoute={getEntityRoute} getContactRoute={getContactRoute}
    />
  }

  function onClick() {
    customMapNotifyMissingNode(custom_map.poi_theme_id, node_id).then((x) => {
      if (x.result === "ok") {
	alert(t("Notification sent"));
      } else {
	alert(t("An error occurred"));
      }
    }).catch((x) => {
      alert(t("An error occurred"));
    })
  }

  return <div class="no-record-found">
    <div style={{display: "flex", alignItems: "center"}}>
      <div className="c-img_buttons">
	{ <span className="c-button_bar-back-icon" onClick={onClose} /> }
      </div>
      <div class="c-resume_infos">
	<span class="c-infos_name">{ node.label }</span>
      </div>
    </div>
    <p>
    { t("No record associated with this item") }
    </p>

    <p>
    { t("Are you interested in this item?") }
    </p>

    <button onClick={onClick} className="c-button publish">
      { t("Send a notification to the administrators") }
    </button>
  </div>
  // col2_width = 50;
}


export { CustomMap };
